import { format } from "date-fns";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EmptyImage from "../../../assets/images/icons/empty_patient.svg";
import filter from "../../../assets/images/icons/filter.svg";
import PlusImage from "../../../assets/images/icons/plus-03.svg";
import {
  DateFilter,
  Drawer,
  GeneralButton,
  GeneralRadio,
  InfiniteScroll,
  SearchComponent,
} from "../../../components";
import { EndPoints } from "../../../Constants/endpoints";
import useFilter from "../../../hooks/useFilter";
import {
  ClosableTag,
  IsProfileCompleted,
  TitleWithArrow,
} from "../../../Share";
import { DoctorsLoader } from "../../search/component";
import AddPatient from "./addPatient/AddPatient";
import { AddReport, PatientCard } from "./component";

const PatientsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    searchParams,
    handleParamsDeleteAll,
    handleParamsRemove,
    handleParamsRemoveMultiple,
  } = useFilter();

  const [searchQuery, setSearchQuery] = useState<any>({
    Start: 0,
    Count: 10,
    Name: undefined,
    SortAscending: false,
    StartDate: undefined as string | undefined,
    EndDate: undefined as string | undefined,
  });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const closeReportModal = () => setIsReportModalOpen(false);
  const handleReportClick = (id: number) => {
    setSelectedId(id);
    setIsReportModalOpen(true);
  };

  const [patientsListData, setPatientsListData] = useState<any[]>();
  const [sortAscending, setSortAscending] = useState(false);
  const [filterType, setFilterType] = useState<"day" | "range">("day");

  const handleFilterTypeChange = (type: "day" | "range") => {
    if (type === filterType) return;

    handleParamsRemoveMultiple(["startDate", "endDate"]);

    setSearchQuery((prev: any) => ({
      ...prev,
      startDate: undefined,
      StartDate: undefined,
      EndDate: undefined,
      Start: 0,
    }));

    setFilterType(type);
  };
  const onSubmit = (search: string) => {
    const dateParam = searchParams.get("startDate");
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");

    setSearchQuery((prev: any) => ({
      ...prev,
      Start: 0,
      Count: 10,
      Name: search ?? undefined,
      SortAscending: sortAscending,
      startDate: filterType === "day" && dateParam ? dateParam : undefined,
      StartDate:
        filterType === "range" && startDateParam ? startDateParam : undefined,
      EndDate:
        filterType === "range" && endDateParam ? endDateParam : undefined,
    }));
  };

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const handleSortOrderChange = (ascending: boolean) => {
    setSortAscending(ascending);
  };

  const submitFilter = () => {
    const dateParam = searchParams.get("startDate");
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");

    setSearchQuery((prev: any) => ({
      ...prev,
      SortAscending: sortAscending,
      startDate: filterType === "day" && dateParam ? dateParam : undefined,
      StartDate:
        filterType === "range" && startDateParam ? startDateParam : undefined,
      EndDate:
        filterType === "range" && endDateParam ? endDateParam : undefined,
      Start: 0,
    }));
    setIsFilterOpen(false);
  };

  const getSelectedDate = () => {
    const dateParam = searchParams.get("startDate");
    return dateParam ? moment(dateParam, "DD-MM-YYYY").toDate() : null;
  };

  const getSelectedRange = () => {
    const startDateParam = searchParams.get("startDate");
    const endDateParam = searchParams.get("endDate");
    if (startDateParam && endDateParam) {
      return {
        start: moment(startDateParam, "DD-MM-YYYY").toDate(),
        end: moment(endDateParam, "DD-MM-YYYY").toDate(),
      };
    }
    return null;
  };

  return (
    <div className="box flex h-full w-full flex-col gap-4 overflow-y-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
        <div className="flex w-full flex-wrap items-center justify-between gap-3">
          <TitleWithArrow title="patients" />
          <div className="flex flex-wrap items-center gap-3">
            <SearchComponent
              searchOn="typing"
              placeholder={t("search_patients")}
              showSearchHistory={false}
              onSearch={onSubmit}
              className="max-xs:w-full"
            /> 
            <GeneralButton
              color="main"
              icon={filter}
              title={t("filter")}
              isActive={isFilterOpen}
              onClick={() => setIsFilterOpen(true)}
            />
            <IsProfileCompleted
              title="complete_your_profile_to_start_adding_patients"
              onAllowedClick={() => setIsAddModalOpen(true)}
            >
              <GeneralButton
                type="button"
                icon={PlusImage}
                className="!gap-0 max-lg:!p-2.5 lg:hidden"
              />
            </IsProfileCompleted>
            <IsProfileCompleted
              title="complete_your_profile_to_start_adding_patients"
              onAllowedClick={() => setIsAddModalOpen(true)}
            >
              <GeneralButton
                type="button"
                title={t("add_patient")}
                icon={PlusImage}
                className="gap-0 !px-4 !py-2.5 max-lg:hidden"
                titleClass="max-lg:hidden"
              />
            </IsProfileCompleted>
          </div>
        </div>
      </div>

      {/* Display selected filters as tags */}
      {filterType === "day" && getSelectedDate() && (
        <ClosableTag
          title={format(getSelectedDate()!, "dd-MM-yyyy")}
          onClose={() => {
            handleParamsRemove("startDate");
            setSearchQuery((prev: any) => ({
              ...prev,
              startDate: undefined,
              Start: 0,
            }));
          }}
        />
      )}

      {filterType === "range" && getSelectedRange() && (
        <ClosableTag
          title={`${format(getSelectedRange()!.start, "dd-MM-yyyy")} - ${format(getSelectedRange()!.end, "dd-MM-yyyy")}`}
          onClose={() => {
            handleParamsRemove("startDate");
            handleParamsRemove("endDate");
            setSearchQuery((prev: any) => ({
              ...prev,
              StartDate: undefined,
              EndDate: undefined,
              Start: 0,
            }));
          }}
        />
      )}

      {isFilterOpen && (
        <Drawer
          isOpen={isFilterOpen}
          customStyle="w-96"
          header={t("patient_filter")}
          onClose={() => {
            setIsFilterOpen(false);
          }}
        >
          <div className="mx-auto flex w-full flex-col gap-6 px-4">
            <div className="space-y-3">
              <GeneralRadio
                label={t("from_newest_to_oldest")}
                checked={!sortAscending}
                onClick={() => handleSortOrderChange(false)}
              />
              <GeneralRadio
                label={t("from_oldest_to_newest")}
                checked={sortAscending}
                onClick={() => handleSortOrderChange(true)}
              />
            </div>

            <div>
              <h3 className="text-md mb-3 font-semibold text-gray-900">
                {t("filter_by_date")}
              </h3>

              <div className="mb-4">
                <GeneralRadio
                  label={t("day")}
                  checked={filterType === "day"}
                  onClick={() => handleFilterTypeChange("day")}
                />
              </div>

              <div className="mb-4">
                <GeneralRadio
                  label={t("range")}
                  checked={filterType === "range"}
                  onClick={() => handleFilterTypeChange("range")}
                />
              </div>
            </div>

            <div className="mt-2">
              {filterType === "day" && (
                <DateFilter
                  name="startDate"
                  title="select_date"
                  placeholderText={t("select_date")}
                />
              )}

              {filterType === "range" && (
                <DateFilter
                  isRange={true}
                  startDateName="startDate"
                  endDateName="endDate"
                  title="select_date_range"
                  placeholderText={t("select_date_range")}
                />
              )}
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            <GeneralButton
              title={t("reset")}
              onClick={() => {
                setSortAscending(false);
                setFilterType("day");
                handleParamsDeleteAll();
                setSearchQuery({
                  Start: 0,
                  Count: 10,
                  Name: undefined,
                  SortAscending: false,
                  StartDate: undefined,
                  EndDate: undefined,
                });
              }}
              fontColor="body-text"
              styleClasses="text-body-text me-0 !h-[45px] !rounded-lg flex-1"
              color="lite"
            />
            <GeneralButton
              onClick={submitFilter}
              styleClasses="!h-[45px] flex-1 !rounded-lg"
              title={`${t("apply_filter")}`}
            />
          </div>
        </Drawer>
      )}
      <>
        <div
          className={`grid h-full w-full flex-1 grow grid-cols-1 flex-wrap gap-x-6 gap-y-2 overflow-auto xl:grid-cols-2 ${(patientsListData?.length ?? 0) > 0 || !patientsListData ? "auto-rows-max" : ""}`}
        >
          {patientsListData?.map((e) => (
            <PatientCard
              key={e.Id}
              item={e}
              to={`/patients_list/${e.Patient.Id}/medical-records`}
              reportPatientClick={() => handleReportClick(e.Patient.Id)}
              bookAppointmentClick={() =>
                navigate(`/appointments/add_new_appointment/${e.Patient.Id}`)
              }
            />
          ))}

          <InfiniteScroll
            Count={14}
            skeletonCount={8}
            endpoint={EndPoints.GetMyPatient}
            getAllParams={searchQuery}
            getData={(data) => setPatientsListData(data)}
            renderSkeleton={<DoctorsLoader />}
            noDataElement={
              !searchParams.get("startDate") && !searchQuery?.Name ? (
                <div className="flex flex-col items-center justify-center gap-4 lg:w-[36%]">
                  <img alt="empty" className="max-w-64" src={EmptyImage} />
                  <h4 className="text-center text-lg font-bold">
                    {t("add_patient_message")}
                  </h4>
                  <IsProfileCompleted
                    title="complete_your_profile_to_start_adding_patients"
                    onAllowedClick={() => setIsAddModalOpen(true)}
                  >
                    <GeneralButton
                      type="button"
                      title={t("add_patient")}
                      icon={PlusImage}
                    />
                  </IsProfileCompleted>
                </div>
              ) : undefined
            }
          />
        </div>
      </>

      <AddPatient
        isAddModalOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />

      {isReportModalOpen && (
        <AddReport
          Id={selectedId ?? 0}
          isReportModalOpen={isReportModalOpen}
          onClose={closeReportModal}
        />
      )}
    </div>
  );
};

export default PatientsList;
